// GENERATED from smartnotedoctor/localization/registry.mjs. Do not edit this copy.
// Canonical language configuration for the Worker and both websites.
// Generate browser copies with: node scripts/sync-language-registry.mjs
// A capability is enabled only after its resources and language checks are ready.
export const DEFAULT_LOCALE = 'en';
export const LANGUAGE_DEFINITIONS = [
  {
    code: 'en', nativeName: 'English', englishName: 'English',
    direction: 'ltr', formatLocale: 'en-GB', fallbacks: [],
    capabilities: { ui: true, marketing: true, audio: true, documents: true },
    speechCode: 'en', stripeLocale: 'en-GB', suggestedCurrency: 'gbp',
  },
  {
    code: 'it', nativeName: 'Italiano', englishName: 'Italian',
    direction: 'ltr', formatLocale: 'it-IT', fallbacks: ['en'],
    capabilities: { ui: true, marketing: true, audio: true, documents: true },
    speechCode: 'it', stripeLocale: 'it', suggestedCurrency: 'eur',
  },
];

const CAPABILITIES = ['ui', 'marketing', 'audio', 'documents'];

export function canonicalizeLocale(value) {
  if (typeof value !== 'string' || !value.trim() || value.length > 100) return null;
  try {
    return Intl.getCanonicalLocales(value.trim().replaceAll('_', '-'))[0] || null;
  } catch (_) {
    return null;
  }
}

// RFC 4647-style lookup: preserve an exact region/script variant when registered,
// then try its parents. Never pick a sibling region or script arbitrarily.
function lookupCandidates(value) {
  const canonical = canonicalizeLocale(value);
  if (!canonical) return [];
  const parts = canonical.split('-');
  const candidates = [];
  while (parts.length) {
    candidates.push(parts.join('-'));
    parts.pop();
    if (parts.length && parts[parts.length - 1].length === 1) parts.pop();
  }
  return candidates;
}

export function createLanguageRegistry(definitions) {
  if (!Array.isArray(definitions)) throw new TypeError('Language definitions must be an array');
  const byCode = new Map();
  for (const definition of definitions) {
    const code = canonicalizeLocale(definition?.code);
    if (!code || byCode.has(code)) throw new Error('Invalid or duplicate language code');
    if (!['ltr', 'rtl'].includes(definition.direction)
        || !canonicalizeLocale(definition.formatLocale)
        || typeof definition.nativeName !== 'string' || !definition.nativeName.trim()
        || typeof definition.englishName !== 'string' || !definition.englishName.trim()
        || !Array.isArray(definition.fallbacks)
        || CAPABILITIES.some(key => typeof definition.capabilities?.[key] !== 'boolean')
        || (definition.capabilities.audio && !canonicalizeLocale(definition.speechCode))) {
      throw new Error('Invalid language metadata: ' + code);
    }
    const fallbacks = definition.fallbacks.map(canonicalizeLocale);
    if (fallbacks.some(value => !value)) throw new Error('Invalid locale fallback: ' + code);
    byCode.set(code, Object.freeze({
      ...definition, code, formatLocale: canonicalizeLocale(definition.formatLocale),
      fallbacks: Object.freeze(fallbacks),
      capabilities: Object.freeze({ ...definition.capabilities }),
    }));
  }
  if (!byCode.get(DEFAULT_LOCALE)?.capabilities.ui) {
    throw new Error('The default UI language must remain enabled');
  }
  const visited = new Set();
  const visiting = new Set();
  const validateFallbacks = code => {
    if (visiting.has(code)) throw new Error('Cyclic locale fallback: ' + code);
    if (visited.has(code)) return;
    const language = byCode.get(code);
    if (!language) throw new Error('Unknown locale fallback: ' + code);
    visiting.add(code);
    language.fallbacks.forEach(validateFallbacks);
    visiting.delete(code);
    visited.add(code);
  };
  byCode.forEach((_, code) => validateFallbacks(code));
  const languages = Object.freeze([...byCode.values()]);
  const assertCapability = capability => {
    if (!CAPABILITIES.includes(capability)) throw new Error('Unknown language capability');
  };
  const list = (capability = 'ui') => {
    assertCapability(capability);
    return languages.filter(language => language.capabilities[capability]);
  };
  const match = (value, capability = 'ui') => {
    assertCapability(capability);
    return lookupCandidates(value).find(code => byCode.get(code)?.capabilities[capability]) || null;
  };
  const resolve = (value, capability = 'ui', fallback = DEFAULT_LOCALE) =>
    match(value, capability) || (fallback ? match(fallback, capability) : null);
  const get = (value, capability = 'ui') => byCode.get(resolve(value, capability)) || null;
  const fallbackChain = (value, capability = 'ui') => {
    assertCapability(capability);
    const result = [];
    const seen = new Set();
    const append = code => {
      if (seen.has(code)) return;
      seen.add(code);
      const language = byCode.get(code);
      if (!language?.capabilities[capability]) return;
      result.push(code);
    };
    lookupCandidates(value).forEach(append);
    for (let index = 0; index < result.length; index += 1) {
      byCode.get(result[index]).fallbacks
        .filter(code => code !== DEFAULT_LOCALE).forEach(append);
    }
    append(DEFAULT_LOCALE);
    return result;
  };
  return Object.freeze({ languages, list, match, resolve, get, fallbackChain });
}

export const languageRegistry = createLanguageRegistry(LANGUAGE_DEFINITIONS);
export const getLanguages = capability => languageRegistry.list(capability);
export const matchLocale = (value, capability) => languageRegistry.match(value, capability);
export const resolveLocale = (value, capability, fallback) => languageRegistry.resolve(value, capability, fallback);
export const getLanguage = (value, capability) => languageRegistry.get(value, capability);
export const localeFallbacks = (value, capability) => languageRegistry.fallbackChain(value, capability);
